import React, { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "primereact/button";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import Layout from "../../component/layout";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { getBillerAddressesURL } from "../../../../utils/urls";
import styles from "./depositReceipt.module.css";

const DepositReceipt = () => {
    const { receiptNumber } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const receiptRef = useRef(null);

    const [receipt, setReceipt] = useState(null);
    const [loading, setLoading] = useState(true);
    // Set when several receipts share this number and the URL doesn't say which one
    const [candidates, setCandidates] = useState([]);
    const [facilityId, setFacilityId] = useState(null);

    // Biller — the facility's default biller address, shown at the top of the slip
    const [billerAddress, setBillerAddress] = useState(null);
    const [billerLoading, setBillerLoading] = useState(false);

    useEffect(() => {
        const init = async () => {
            const fid = await getItem("selectedFacilityId");
            setFacilityId(fid);
            if (fid && receiptNumber) {
                fetchReceipt(fid, receiptNumber);
                fetchDefaultBillerAddress(fid);
            }
        };
        init();
    }, [receiptNumber, searchParams]);

    const fetchReceipt = async (fid, receiptNum) => {
        setLoading(true);
        try {
            const response = await makeRequest2(
                `/api/app/levy_management/levy_deposits/get_receipt/${fid}/${receiptNum}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`,
                "GET"
            );

            if (response.success && response.data.data?.ambiguous) {
                setCandidates(response.data.data.candidates || []);
            } else if (response.success) {
                setCandidates([]);
                setReceipt(response.data.data);
            } else {
                toastify(response.error || "Failed to fetch receipt", "error");
            }
        } catch (error) {
            console.error("Error fetching receipt:", error);
            toastify("Error fetching receipt", "error");
        } finally {
            setLoading(false);
        }
    };

    // Biller addresses are a flat, facility-level list (same list managed in
    // BillerAddressTab.jsx) — not resolved per-levy. Pick the one flagged
    // isDefault; fall back to the first entry if none is marked default.
    const fetchDefaultBillerAddress = async (fid) => {
        setBillerLoading(true);
        try {
            const response = await makeRequest2(`${getBillerAddressesURL}/${fid}`, "GET");

            if (response && response.success && response.data) {
                let addresses = [];
                if (Array.isArray(response.data)) {
                    addresses = response.data;
                } else if (response.data.data && Array.isArray(response.data.data)) {
                    addresses = response.data.data;
                } else if (response.data.addresses && Array.isArray(response.data.addresses)) {
                    addresses = response.data.addresses;
                } else if (typeof response.data === "object" && response.data !== null) {
                    addresses = [response.data];
                }

                const defaultAddress = addresses.find((a) => a.isDefault) || addresses[0] || null;
                setBillerAddress(defaultAddress);
            }
        } catch (error) {
            console.error("Error fetching biller address:", error);
        } finally {
            setBillerLoading(false);
        }
    };

    const handleDownloadPDF = async () => {
        const element = document.getElementById("printable-receipt");
        if (!element) return;

        try {
            setLoading(true);
            toastify("Generating PDF...", "info");

            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: "#ffffff",
                width: element.scrollWidth,
                height: element.scrollHeight,
                windowWidth: element.scrollWidth,
                windowHeight: element.scrollHeight,
            });

            const imgData = canvas.toDataURL("image/jpeg", 1.0);
            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a5",
            });

            const pdfWidth = 148;
            const pdfHeight = 210;
            const imgWidth = pdfWidth;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            if (imgHeight <= pdfHeight) {
                pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight);
            } else {
                const scale = pdfHeight / imgHeight;
                const scaledWidth = imgWidth * scale;
                const x = (pdfWidth - scaledWidth) / 2;
                pdf.addImage(imgData, "JPEG", x, 0, scaledWidth, pdfHeight);
            }

            pdf.save(`Deposit_Receipt_${receiptNumber}.pdf`);
            toastify("PDF downloaded successfully!", "success");
        } catch (error) {
            console.error("PDF generation error:", error);
            toastify("Failed to generate PDF. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (date) => {
        if (!date) return "N/A";
        const dateObj = new Date(date);
        const day = dateObj.getDate();
        const month = dateObj.toLocaleDateString("en-US", { month: "short" });
        const year = dateObj.getFullYear();
        const getOrdinalSuffix = (d) => {
            if (d > 3 && d < 21) return "th";
            switch (d % 10) {
                case 1: return "st";
                case 2: return "nd";
                case 3: return "rd";
                default: return "th";
            }
        };
        return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
    };

    const formatAmount = (amount) => {
        if (amount === undefined || amount === null) return "KES 0.00";
        const numericAmount = parseFloat(amount);
        const formattedNumber = numericAmount.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        return `KES ${formattedNumber}`;
    };

    const getCustomerName = () => {
        if (!receipt?.customerId) return "N/A";
        const c = receipt.customerId;
        return `${c.firstName || ""} ${c.lastName || ""}`.trim() || c.name || "N/A";
    };

    const getUnitName = () => {
        return receipt?.unitId?.name || receipt?.unitId?.unitNumber || "N/A";
    };

    const getDescription = () => {
        return receipt?.contractId?.levyId?.levyName || receipt?.contractId?.levyName || "Service Charge";
    };

    const getBillerAddressLine = () => {
        if (!billerAddress) return "";
        const parts = [billerAddress.address, billerAddress.city, billerAddress.country].filter(Boolean);
        return parts.join(", ");
    };

    const getBillerContactLine = () => {
        if (!billerAddress) return "";
        const parts = [billerAddress.phone, billerAddress.email].filter(Boolean);
        return parts.join(" · ");
    };

    if (loading) {
        return (
            <Layout>
                <div className="d-flex justify-content-center align-items-center" style={{ height: "70vh" }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <span className="ms-2">Loading receipt details...</span>
                </div>
            </Layout>
        );
    }

    if (candidates.length > 0) {
        return (
            <Layout>
                <div className="container py-5" style={{ maxWidth: 720 }}>
                    <h4>Which receipt?</h4>
                    <p className="text-muted">
                        More than one receipt uses the number {receiptNumber}. Choose the customer:
                    </p>
                    <div className="list-group">
                        {candidates.map((c) => (
                            <button
                                key={c._id}
                                type="button"
                                className="list-group-item list-group-item-action d-flex justify-content-between"
                                onClick={() =>
                                    navigate(`/facility/levy_management/deposit-receipt/${receiptNumber}?receiptId=${c._id}`)
                                }
                            >
                                <span>
                                    <strong>{c.customerName || "Unknown customer"}</strong>
                                    {c.unitName ? ` · Unit ${c.unitName}` : ""}
                                </span>
                                <span className="text-muted">
                                    {c.datePaid ? new Date(c.datePaid).toLocaleDateString() : "Not paid"}
                                    {c.paymentReference ? ` · ${c.paymentReference}` : ""}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            </Layout>
        );
    }

    if (!receipt) {
        return (
            <Layout>
                <div className="d-flex justify-content-center align-items-center" style={{ height: "70vh" }}>
                    <div className="text-center">
                        <i className="ti ti-receipt-off" style={{ fontSize: "4rem", color: "#dc3545" }}></i>
                        <h4 className="mt-3">Receipt Not Found</h4>
                        <p className="text-muted">The receipt you're looking for doesn't exist or has been removed.</p>
                        <Button label="Go Back" icon="ti ti-arrow-left" onClick={() => navigate(-1)} className="btn btn-primary mt-2" />
                    </div>
                </div>
            </Layout>
        );
    }

    const lineItems = receipt.lineItems || [];
    const subTotal = lineItems.reduce((sum, item) => sum + (item.unitAmount * item.quantity), 0);
    const taxTotal = lineItems.reduce((sum, item) => sum + (item.taxAmount || 0), 0);
    const totalCharges = receipt.totalCharges || subTotal + taxTotal;
    const amountPaid = receipt.amountPaid || 0;
    const balanceDue = receipt.balanceDue ?? (totalCharges - amountPaid);
    const isOverpaid = balanceDue < 0;

    return (
        <Layout>
            <div className={styles.pageContainer}>
                {/* Action Bar */}
                <div className={styles.actionCard}>
                    <div className={styles.actionCardLeft}>
                        <Link to="#" onClick={() => navigate(-1)} className={styles.backLink}>
                            <i className="ti ti-arrow-narrow-left"></i>
                            <span>Back</span>
                        </Link>
                    </div>
                    <div className={styles.actionCardRight}>
                        <Button
                            label="Download PDF"
                            icon="ti ti-download"
                            onClick={handleDownloadPDF}
                            className="btn btn-primary"
                            loading={loading}
                        />
                    </div>
                </div>

                <div className={styles.receiptWrapper}>
                    <div id="printable-receipt" ref={receiptRef} className={styles.receiptPaper}>

                        <div className={styles.receiptHeader}>
                            <h4 className={styles.receiptTitle}>Receipt</h4>

                            {!billerLoading && billerAddress && (
                                <div className={styles.billerBlock}>
                                    <div className={styles.billerName}>{billerAddress.companyName}</div>
                                    {getBillerAddressLine() && (
                                        <div className={styles.billerLine}>{getBillerAddressLine()}</div>
                                    )}
                                    {getBillerContactLine() && (
                                        <div className={styles.billerLine}>{getBillerContactLine()}</div>
                                    )}
                                </div>
                            )}

                            <div className={styles.receiptMeta}>
                                <span>{receipt.receiptNumber ? `#${receipt.receiptNumber}` : ""}</span>
                            </div>
                        </div>

                        <div className={styles.divider}></div>

                        {/* Receipt details */}
                        <div className={styles.section}>
                            <div className={styles.row}>
                                <span className={styles.label}>Reference No</span>
                                <span className={styles.value}>{receipt.paymentReference || "N/A"}</span>
                            </div>
                            <div className={styles.row}>
                                <span className={styles.label}>Paid Date</span>
                                <span className={styles.value}>{formatDate(receipt.datePaid)}</span>
                            </div>
                            <div className={styles.row}>
                                <span className={styles.label}>Description</span>
                                <span className={styles.value}>{getDescription()}</span>
                            </div>
                        </div>

                        <div className={styles.dividerDashed}></div>

                        {/* Received From */}
                        <div className={styles.section}>
                            <div className={styles.row}>
                                <span className={styles.label}>Received From</span>
                                <span className={styles.value}>{getCustomerName()}</span>
                            </div>
                            <div className={styles.row}>
                                <span className={styles.label}>Unit</span>
                                <span className={styles.value}>{getUnitName()}</span>
                            </div>
                        </div>

                        <div className={styles.dividerDashed}></div>

                        {/* Line items */}
                        <div className={styles.section}>
                            <div className={styles.lineTitle}>Items</div>
                            {lineItems.length > 0 ? (
                                lineItems.map((item, index) => {
                                    const lineTotal = (item.unitAmount * item.quantity) + (item.taxAmount || 0);
                                    return (
                                        <div key={index} className={styles.itemRow}>
                                            <span className={styles.itemDesc}>
                                                {item.description || "N/A"}
                                                {item.quantity > 1 ? ` (x${item.quantity})` : ""}
                                            </span>
                                            <span className={styles.itemAmount}>{formatAmount(lineTotal)}</span>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="text-muted small">No items found</div>
                            )}
                        </div>

                        <div className={styles.dividerDashed}></div>

                        {/* Calculations */}
                        <div className={styles.section}>
                            <div className={styles.row}>
                                <span>Subtotal</span>
                                <span>{formatAmount(subTotal)}</span>
                            </div>
                            {taxTotal > 0 && (
                                <div className={styles.row}>
                                    <span>Tax</span>
                                    <span>{formatAmount(taxTotal)}</span>
                                </div>
                            )}
                            <div className={styles.subtotal}>
                                <span>Total Charges</span>
                                <span>{formatAmount(totalCharges)}</span>
                            </div>
                            <div className={styles.row}>
                                <span>Amount Paid</span>
                                <span>{formatAmount(amountPaid)}</span>
                            </div>
                        </div>

                        <div className={styles.divider}></div>

                        <div className={styles.section}>
                            <div className={styles.grandTotal}>
                                <span>{isOverpaid ? "OVERPAYMENT" : "BALANCE DUE"}</span>
                                <span>{isOverpaid ? formatAmount(Math.abs(balanceDue)) : formatAmount(balanceDue)}</span>
                            </div>
                        </div>

                        <div className={styles.receiptFooter}>
                            <p>Thank you for your payment!</p>
                            <p>This is a computer generated receipt.</p>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default DepositReceipt;